import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type Room,
  createLocalAudioTrack,
  createLocalVideoTrack,
  type LocalAudioTrack,
  type LocalVideoTrack,
} from 'livekit-client';
import { apiRequest } from '@/lib/queryClient';
import { useCall, createCallRoom } from '@/contexts/call-context';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useQuery } from '@tanstack/react-query';
import {
  getSavedDevicePreferences,
  getSkipPreCallLobby,
  isSpeakerSelectionSupported,
  saveDevicePreference,
  saveSkipPreCallLobby,
  useMediaDevices,
} from '@/hooks/use-media-devices';
import { useToast } from '@/hooks/use-toast';
import { DeviceMenu } from '@/components/calls/DeviceMenu';
import { AlertTriangle, Loader2, Mic, MicOff, Phone, PhoneOff, RefreshCw, Video, VideoOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UserLite {
  id: number;
  firstName?: string;
  lastName?: string;
  shortCode?: string;
  colorCode?: string;
}

function formatInviteeNames(users: UserLite[] | undefined, ids: number[]): string {
  if (!users || ids.length === 0) return '';
  const names = ids.map((id) => {
    const u = users.find((x) => x.id === id);
    if (!u) return `User ${id}`;
    const first = u.firstName?.trim();
    const lastInitial = u.lastName?.trim()?.[0];
    if (first && lastInitial) return `${first} ${lastInitial}.`;
    if (first) return first;
    return u.shortCode ?? `User ${id}`;
  });
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/**
 * Pre-join "lobby" shown for OUTBOUND calls only. Lets the user verify their
 * camera, watch a live mic level meter, and switch devices before any other
 * participant is rung. Per the product spec, accepting an incoming call
 * deliberately bypasses this — the caller is already waiting on the line and
 * routing the callee through a device check would create awkward dead air.
 *
 * Critical implementation choices:
 *  - Tracks are acquired with `createLocalTracks` (i.e. directly via
 *    `getUserMedia`) rather than reusing in-call hooks that assume an active
 *    LiveKit Room. Without an active room, the in-call DeviceMenu callbacks
 *    (`switchCamera`, etc.) would crash on `room.switchActiveDevice`.
 *  - On confirm, the LIVE `LocalAudioTrack` / `LocalVideoTrack` instances are
 *    handed off to `connectToRoom` for direct publish. We never call
 *    `track.stop()` on the success path — doing so would tear down the
 *    underlying MediaStreamTrack we're about to publish.
 *  - The mic level meter wires the MediaStreamTrack into a Web Audio
 *    `AnalyserNode` and is INTENTIONALLY never connected to
 *    `audioContext.destination`. Doing so would loop the local mic to the
 *    local speakers, producing immediate echo/feedback.
 */
export function PreCallLobby() {
  const {
    pendingOutgoing,
    confirmOutgoingFromLobby,
    cancelOutgoingFromLobby,
  } = useCall();

  if (!pendingOutgoing) return null;
  // Re-key on every fresh lobby-open so all internal refs/state are
  // discarded between consecutive outbound flows. Without this, a quick
  // cancel-then-call could resurrect stale tracks.
  return (
    <PreCallLobbyImpl
      key={`${pendingOutgoing.channelId}:${pendingOutgoing.type}:${pendingOutgoing.inviteeUserIds.join(',')}:${pendingOutgoing.projectId ?? ''}`}
      pendingOutgoing={pendingOutgoing}
      onConfirm={confirmOutgoingFromLobby}
      onCancel={cancelOutgoingFromLobby}
    />
  );
}

function PreCallLobbyImpl({
  pendingOutgoing,
  onConfirm,
  onCancel,
}: {
  pendingOutgoing: NonNullable<ReturnType<typeof useCall>['pendingOutgoing']>;
  onConfirm: ReturnType<typeof useCall>['confirmOutgoingFromLobby'];
  onCancel: ReturnType<typeof useCall>['cancelOutgoingFromLobby'];
}) {
  const { toast } = useToast();
  const isVideo = pendingOutgoing.type === 'video';

  const { devices, hasLabels, speakerSupported, ensureLabels, refresh } = useMediaDevices(true);
  const { data: users } = useQuery<UserLite[]>({ queryKey: ['/api/users'] });
  const inviteeNames = useMemo(
    () => formatInviteeNames(users, pendingOutgoing.inviteeUserIds),
    [users, pendingOutgoing.inviteeUserIds],
  );

  const [audioTrack, _setAudioTrack] = useState<LocalAudioTrack | null>(null);
  const [videoTrack, _setVideoTrack] = useState<LocalVideoTrack | null>(null);
  // Refs mirror the latest tracks so the unmount-only cleanup effect
  // (further down) can read the freshest values without depending on them
  // (which would re-fire the cleanup on every switch and re-introduce the
  // "switching mic kills camera" bug). Update via setAudio / setVideo so
  // the ref stays in lock-step with state — never call _setAudioTrack /
  // _setVideoTrack directly.
  const audioTrackRef = useRef<LocalAudioTrack | null>(null);
  const videoTrackRef = useRef<LocalVideoTrack | null>(null);
  // Flipped to false in the unmount cleanup. Async device switches consult
  // it after their createLocal*Track promise resolves so a switch that
  // races with cancel/HMR/route-change doesn't push a fresh track into a
  // dead component (which would leak the camera/mic, since unmount cleanup
  // already ran on the *previous* refs).
  const mountedRef = useRef(true);
  const setAudio = useCallback((next: LocalAudioTrack | null) => {
    audioTrackRef.current = next;
    _setAudioTrack(next);
  }, []);
  const setVideo = useCallback((next: LocalVideoTrack | null) => {
    videoTrackRef.current = next;
    _setVideoTrack(next);
  }, []);
  // Live device IDs reflect what the browser actually selected (which may
  // differ from what we asked for, e.g. when 'default' resolves to a real
  // deviceId). Drives the dropdown checkmarks.
  const [micId, setMicId] = useState<string | undefined>(undefined);
  const [camId, setCamId] = useState<string | undefined>(undefined);
  const [speakerId, setSpeakerId] = useState<string | undefined>(undefined);

  const [acquiring, setAcquiring] = useState(true);
  const [micError, setMicError] = useState<string | null>(null);
  const [camError, setCamError] = useState<string | null>(null);
  // Permission state is split out from the generic error so the UI can
  // distinguish "device unavailable" (toast/inline error) from "user
  // blocked permission" (recovery panel with Try Again).
  const [micBlocked, setMicBlocked] = useState(false);
  const [camBlocked, setCamBlocked] = useState(false);
  const [joining, setJoining] = useState(false);
  // Track whether the user has explicitly confirmed — used by cleanup to
  // decide whether to stop the tracks (cancel) or hand them off (join).
  const confirmedRef = useRef(false);

  const [skipNextTime, setSkipNextTime] = useState(false);

  // ── Mic level meter ───────────────────────────────────────────────────────
  // We feed the LocalAudioTrack's underlying MediaStreamTrack into a Web
  // Audio AnalyserNode. The `source.connect(analyser)` graph terminates at
  // the analyser — we never `analyser.connect(audioCtx.destination)`, which
  // would route the mic back to the local speakers and cause immediate
  // echo/feedback. The analyser pulls samples on demand via
  // `getByteTimeDomainData`, so no extra buffering is needed.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const [micLevel, setMicLevel] = useState(0);

  const stopMeter = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    try { sourceRef.current?.disconnect(); } catch {}
    sourceRef.current = null;
    analyserRef.current = null;
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (ctx && ctx.state !== 'closed') {
      try { void ctx.close(); } catch {}
    }
    setMicLevel(0);
  }, []);

  const startMeter = useCallback((track: LocalAudioTrack) => {
    stopMeter();
    const mst = track.mediaStreamTrack;
    if (!mst) return;
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return;
    let ctx: AudioContext;
    try {
      ctx = new Ctor();
    } catch {
      return;
    }
    let source: MediaStreamAudioSourceNode;
    try {
      source = ctx.createMediaStreamSource(new MediaStream([mst]));
    } catch (e) {
      console.warn('[Lobby] meter source failed', e);
      try { void ctx.close(); } catch {}
      return;
    }
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.6;
    // Deliberately NOT: analyser.connect(ctx.destination). Connecting
    // closes the loop mic → speakers and produces audible feedback.
    source.connect(analyser);
    audioCtxRef.current = ctx;
    sourceRef.current = source;
    analyserRef.current = analyser;
    const buf = new Uint8Array(analyser.fftSize);
    const tick = () => {
      const a = analyserRef.current;
      if (!a) return;
      a.getByteTimeDomainData(buf);
      let sumSquares = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sumSquares += v * v;
      }
      const rms = Math.sqrt(sumSquares / buf.length);
      // Normalise: speech RMS sits roughly in 0.05–0.30. Scale up so the
      // meter reaches full bar on loud speech without clipping silence.
      setMicLevel(Math.max(0, Math.min(1, rms * 3)));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [stopMeter]);

  // ── Initial track acquisition ─────────────────────────────────────────────
  // Tracked in a ref rather than as a closure variable so the standalone
  // `acquireTracks` callback (used by Try Again) can also abort cleanly if
  // the user cancels mid-retry.
  const acquireRunIdRef = useRef(0);

  // Single shared acquire path so the initial mount AND the "Try again"
  // button after a permission denial use identical logic. Returns nothing —
  // updates state via setters and the audio/video track refs.
  const acquireTracks = useCallback(async () => {
    const runId = ++acquireRunIdRef.current;
    setAcquiring(true);
    setMicError(null);
    setCamError(null);
    setMicBlocked(false);
    setCamBlocked(false);
    stopMeter();
    const saved = getSavedDevicePreferences();

    // Mic
    let mic: LocalAudioTrack | null = null;
    try {
      mic = await createLocalAudioTrack(
        saved.audioinput ? { deviceId: { exact: saved.audioinput } } : undefined,
      );
    } catch (e: any) {
      if (e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError') {
        // User clicked Block. Without specific handling we'd hang in
        // "Starting microphone…" forever; instead, surface a recovery
        // panel with a Try Again button.
        if (runId === acquireRunIdRef.current) setMicBlocked(true);
      } else if (saved.audioinput && (e?.name === 'OverconstrainedError' || e?.name === 'NotFoundError')) {
        // Saved mic gone — clear preference and retry with system default.
        saveDevicePreference('audioinput', null);
        try {
          mic = await createLocalAudioTrack();
        } catch (e2: any) {
          console.warn('[Lobby] mic acquire fallback failed', e2);
          if (runId === acquireRunIdRef.current) {
            if (e2?.name === 'NotAllowedError' || e2?.name === 'PermissionDeniedError') {
              setMicBlocked(true);
            } else {
              setMicError(e2?.message || 'Could not access microphone');
            }
          }
        }
      } else {
        console.warn('[Lobby] mic acquire failed', e);
        if (runId === acquireRunIdRef.current) setMicError(e?.message || 'Could not access microphone');
      }
    }

    // Camera (video calls only)
    let cam: LocalVideoTrack | null = null;
    if (isVideo) {
      try {
        cam = await createLocalVideoTrack(
          saved.videoinput ? { deviceId: { exact: saved.videoinput } } : undefined,
        );
      } catch (e: any) {
        if (e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError') {
          if (runId === acquireRunIdRef.current) setCamBlocked(true);
        } else if (saved.videoinput && (e?.name === 'OverconstrainedError' || e?.name === 'NotFoundError')) {
          saveDevicePreference('videoinput', null);
          try {
            cam = await createLocalVideoTrack();
          } catch (e2: any) {
            console.warn('[Lobby] camera acquire fallback failed', e2);
            if (runId === acquireRunIdRef.current) {
              if (e2?.name === 'NotAllowedError' || e2?.name === 'PermissionDeniedError') {
                setCamBlocked(true);
              } else {
                setCamError(e2?.message || 'Could not access camera');
              }
            }
          }
        } else {
          console.warn('[Lobby] camera acquire failed', e);
          if (runId === acquireRunIdRef.current) setCamError(e?.message || 'Could not access camera');
        }
      }
    }

    // A newer acquire run started — discard our results so we don't clobber
    // it. Stop anything we did manage to grab.
    if (runId !== acquireRunIdRef.current) {
      try { mic?.stop(); } catch {}
      try { cam?.stop(); } catch {}
      return;
    }

    setAudio(mic);
    setVideo(cam);
    setMicId(mic?.mediaStreamTrack?.getSettings().deviceId);
    setCamId(cam?.mediaStreamTrack?.getSettings().deviceId);
    setSpeakerId(saved.audiooutput);
    setAcquiring(false);
    // Refresh device list now that permission has been granted — the
    // `label` fields populate post-getUserMedia.
    void refresh();
    if (mic) startMeter(mic);
  }, [isVideo, refresh, startMeter, stopMeter]);

  useEffect(() => {
    void acquireTracks();
    return () => {
      // Bump the run id so any in-flight acquire from this mount discards
      // its results when it eventually settles. The cleanup useEffect
      // below handles stopping the actual tracks once they're in state.
      acquireRunIdRef.current++;
    };
  // Run once on mount only — we don't want acquireTracks identity changes
  // (which can happen if any of its deps re-create) to trigger a full
  // re-acquire and a cascade of getUserMedia prompts. Manual retry is
  // surfaced via the Try Again button.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-warm the LiveKit WS + ICE fetch on the same Room we'll hand off
  // to connectToRoom on Join. URL-only (no token) so we don't ring
  // invitees before the user clicks Join.
  const warmupRoomRef = useRef<Room | null>(null);
  const warmupHandedOffRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    const room = createCallRoom();
    warmupRoomRef.current = room;
    (async () => {
      const res = await apiRequest('GET', '/api/calls/livekit-url');
      if (!res.ok || cancelled) return;
      const { url } = await res.json();
      if (!url || cancelled) return;
      void room.prepareConnection(url).catch(() => {});
    })().catch(() => {});
    return () => {
      cancelled = true;
      const r = warmupRoomRef.current;
      warmupRoomRef.current = null;
      // Ownership transfers to connectToRoom once handleJoin sets the flag.
      if (r && !warmupHandedOffRef.current) void r.disconnect();
    };
  }, []);

  // Attach the camera track to the preview <video>. We re-attach whenever
  // the track instance changes (e.g. after switching cameras).
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    if (!videoTrack) return;
    const el = videoElRef.current;
    if (!el) return;
    videoTrack.attach(el);
    return () => { try { videoTrack.detach(el); } catch {} };
  }, [videoTrack]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  // If the user clicks "Join", `confirmedRef` is set BEFORE we update the
  // pending-outgoing state (which unmounts us), so this cleanup leaves the
  // tracks alive for the room to publish. On any other unmount (Cancel,
  // route change, hot reload, etc.) we stop the tracks fully so the camera
  // light goes off and the mic is released.
  //
  // We deliberately mirror audioTrack / videoTrack into refs and key the
  // cleanup effect to `[]` so it only runs at unmount. An earlier version
  // listed both tracks as dependencies, which meant switching the mic
  // re-fired the cleanup with the previously-captured (still-current!)
  // videoTrack and stopped it — blanking the camera preview. Same hazard
  // existed in reverse when switching cameras (silently muting the mic).
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopMeter();
      if (!confirmedRef.current) {
        try { audioTrackRef.current?.stop(); } catch {}
        try { videoTrackRef.current?.stop(); } catch {}
      }
    };
  // Empty deps on purpose: refs above are kept in sync at every mutation
  // site via setAudio / setVideo, so this cleanup always reads the latest
  // tracks without re-firing on each switch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Device switching inside the lobby ─────────────────────────────────────
  // Each switch tears down the meter, stops the old track, acquires a new
  // one with the chosen deviceId, persists the choice, and re-arms the
  // meter (mic) or re-attaches via the videoTrack effect above (camera).
  const switchMicLobby = useCallback(async (deviceId: string) => {
    if (deviceId === micId) return;
    stopMeter();
    const old = audioTrack;
    try {
      const next = await createLocalAudioTrack({ deviceId: { exact: deviceId } });
      // If the user cancelled / HMR / route-changed while we were
      // acquiring, the unmount cleanup has already stopped `old` and is
      // gone — there's nothing to attach `next` to. Stop it immediately
      // so the mic indicator turns off.
      if (!mountedRef.current) {
        try { next.stop(); } catch {}
        return;
      }
      try { old?.stop(); } catch {}
      setAudio(next);
      setMicId(next.mediaStreamTrack?.getSettings().deviceId ?? deviceId);
      setMicError(null);
      saveDevicePreference('audioinput', deviceId);
      startMeter(next);
    } catch (e: any) {
      console.warn('[Lobby] mic switch failed', e);
      if (!mountedRef.current) return;
      // Old track is still alive — re-arm the meter so the user isn't left
      // staring at a dead bar while we surface the error.
      if (old) startMeter(old);
      toast({
        title: 'Could not switch microphone',
        description: e?.message || 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [audioTrack, micId, stopMeter, startMeter, setAudio, toast]);

  const switchCamLobby = useCallback(async (deviceId: string) => {
    if (deviceId === camId) return;
    const old = videoTrack;
    try {
      const next = await createLocalVideoTrack({ deviceId: { exact: deviceId } });
      // Same race guard as switchMicLobby — if we unmounted mid-acquire,
      // stop the freshly created camera track so the camera light goes
      // off instead of staying on indefinitely.
      if (!mountedRef.current) {
        try { next.stop(); } catch {}
        return;
      }
      try { old?.stop(); } catch {}
      setVideo(next);
      setCamId(next.mediaStreamTrack?.getSettings().deviceId ?? deviceId);
      setCamError(null);
      saveDevicePreference('videoinput', deviceId);
    } catch (e: any) {
      console.warn('[Lobby] camera switch failed', e);
      if (!mountedRef.current) return;
      toast({
        title: 'Could not switch camera',
        description: e?.message || 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [videoTrack, camId, setVideo, toast]);

  const switchSpeakerLobby = useCallback((deviceId: string) => {
    // Speaker selection isn't a track — there's nothing audible to preview
    // before the call (no remote audio yet). Persist the choice and let
    // `connectToRoom` apply it via switchActiveDevice once the room exists.
    if (!isSpeakerSelectionSupported()) return;
    setSpeakerId(deviceId);
    saveDevicePreference('audiooutput', deviceId);
    toast({ title: 'Speaker selected', description: 'Will be applied when the call starts.' });
  }, [toast]);

  // Initialise the skip checkbox from the persisted setting so the user
  // sees whether they're currently opted out.
  useEffect(() => {
    setSkipNextTime(getSkipPreCallLobby());
  }, []);

  const handleJoin = useCallback(async () => {
    if (joining) return;
    setJoining(true);
    saveSkipPreCallLobby(skipNextTime);
    // Stop the meter graph BEFORE the audio track ships off to LiveKit —
    // the AnalyserNode keeps the AudioContext alive otherwise and Chromium
    // logs noisy "node disconnected from already-stopped track" warnings
    // when the track later goes away.
    stopMeter();
    // Detach the camera track from our preview <video> BEFORE the lobby
    // unmounts. Without this, React tears down the <video> element while
    // the track is still attached, leaving a stale renderer binding for
    // ~1 frame; the user perceives a black flicker as the InCallWindow
    // re-attaches the same MediaStreamTrack to its participant tile.
    // Explicit detach surrenders the binding cleanly so re-attach happens
    // immediately rather than after the renderer has caught up with the
    // teardown.
    if (videoTrack && videoElRef.current) {
      try { videoTrack.detach(videoElRef.current); } catch {}
    }
    confirmedRef.current = true;
    // Hand the warm-up Room off; cleanup useEffect will skip disconnect.
    const handoffRoom = warmupRoomRef.current ?? undefined;
    if (handoffRoom) warmupHandedOffRef.current = true;
    await onConfirm({
      audioTrack: audioTrack ?? undefined,
      videoTrack: videoTrack ?? undefined,
      speakerDeviceId: speakerId,
      room: handoffRoom,
    });
  }, [joining, skipNextTime, audioTrack, videoTrack, speakerId, onConfirm, stopMeter]);

  const handleCancel = useCallback(() => {
    // Cleanup useEffect handles stopping tracks since `confirmedRef` is
    // false here — just ask the context to clear the pending state.
    onCancel();
  }, [onCancel]);

  // Render a series of mic-level segments (10 bars). Each bar lights when
  // the normalised level crosses its threshold.
  const meterBars = useMemo(() => {
    const total = 10;
    const filled = Math.round(micLevel * total);
    return Array.from({ length: total }, (_, i) => i < filled);
  }, [micLevel]);

  // Joining without a mic is technically possible (LiveKit lets you publish
  // nothing) but it's a strictly worse experience than the user expects —
  // they'd be in a "call" everyone else can see but nobody can hear them
  // speak. Block the Join button entirely until they have a working mic.
  const canJoinAudio = !!audioTrack;
  // For video calls: if the camera was denied but the mic is fine, offer
  // an explicit "Join with audio only" affordance rather than silently
  // joining with a missing track. The label change is also a hint to fix
  // browser permissions if they wanted video.
  const audioOnlyVideoJoin = isVideo && !videoTrack && !!audioTrack;
  // Render the permission-recovery panel in lieu of the rest of the lobby
  // when a denial completely blocks the call (audio missing) — there's no
  // useful preview to render and forcing the user to scroll past blank
  // pickers to find the Try Again button would be punishing.
  const showPermissionPanel = (micBlocked || (isVideo && camBlocked)) && !canJoinAudio;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      data-testid="pre-call-lobby"
    >
      <div className="bg-card border border-border rounded-2xl shadow-2xl p-5 w-full max-w-md flex flex-col gap-4 animate-in fade-in zoom-in-95">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-base font-semibold flex items-center gap-2">
              {isVideo ? <Video className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
              Ready to join?
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {inviteeNames
                ? <>Calling <span className="text-foreground font-medium">{inviteeNames}</span> · check your devices first</>
                : 'Check your microphone and camera before joining.'}
            </div>
          </div>
        </div>

        {/* Permission-blocked recovery panel — shown only when the call
            literally cannot proceed (no mic). Replaces the rest of the
            lobby with a clear next-step rather than letting the user stare
            at empty pickers and a disabled Join. */}
        {showPermissionPanel && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40 p-3 flex gap-2 items-start"
            data-testid="lobby-permission-blocked"
          >
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900 dark:text-amber-200 space-y-1">
              <div className="font-medium">
                {micBlocked && (isVideo && camBlocked)
                  ? 'Microphone and camera are blocked'
                  : micBlocked
                    ? 'Microphone is blocked'
                    : 'Camera is blocked'}
              </div>
              <div className="opacity-90">
                Click the camera/microphone icon in your browser's address bar,
                allow access for this site, then click Try Again.
              </div>
            </div>
          </div>
        )}

        {/* Camera preview (video calls only). Hidden when the recovery
            panel is up — it would just show "Camera unavailable" twice. */}
        {isVideo && !showPermissionPanel && (
          <div className="relative bg-black rounded-lg overflow-hidden aspect-video border border-border">
            {videoTrack ? (
              <video
                ref={videoElRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                data-testid="lobby-video-preview"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground text-xs gap-1.5 bg-muted px-4 text-center">
                {acquiring ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Starting camera…</>
                ) : camBlocked ? (
                  <>
                    <VideoOff className="h-4 w-4" />
                    <span>Camera permission blocked — joining audio-only.</span>
                  </>
                ) : (
                  <><VideoOff className="h-4 w-4" /> {camError || 'Camera unavailable'}</>
                )}
              </div>
            )}
          </div>
        )}

        {/* Mic meter, device pickers, and skip-lobby preference are all
            suppressed when the permission recovery panel is up — the Try
            Again button is the only useful interaction in that state, so
            hiding the empty UI reduces noise. */}
        {!showPermissionPanel && (
        <>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              {audioTrack ? <Mic className="h-3.5 w-3.5" /> : <MicOff className="h-3.5 w-3.5" />}
              Microphone
            </span>
            <span className="text-[10px]">
              {audioTrack
                ? 'Speak to test the level'
                : (acquiring ? 'Starting microphone…' : (micError || 'Microphone unavailable'))}
            </span>
          </div>
          <div
            className="flex gap-0.5 h-2.5"
            data-testid="lobby-mic-meter"
            aria-label="Microphone level"
          >
            {meterBars.map((on, i) => (
              <div
                key={i}
                className={cn(
                  'flex-1 rounded-sm transition-colors',
                  on
                    ? i < 6
                      ? 'bg-green-500'
                      : i < 8
                        ? 'bg-amber-500'
                        : 'bg-red-500'
                    : 'bg-muted',
                )}
              />
            ))}
          </div>
        </div>

        {/* Device pickers — same DeviceMenu used in the in-call control bar
            so users see consistent UX before and after joining. */}
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs text-muted-foreground">Microphone device</Label>
            <DeviceMenu
              kind="audioinput"
              devices={devices.audioinput}
              currentId={micId}
              onSelect={(id) => { void switchMicLobby(id); }}
              onEnsureLabels={() => { void ensureLabels(); }}
              disabled={acquiring}
              triggerLabel="Microphone"
              triggerVariant="button"
              hasLabels={hasLabels}
              trigger={
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 max-w-[200px] truncate justify-start gap-1.5"
                  data-testid="lobby-device-picker-mic"
                  disabled={acquiring}
                >
                  <Mic className="h-3 w-3 shrink-0" />
                  <span className="truncate text-[11px]">
                    {devices.audioinput.find((d) => d.deviceId === micId)?.label
                      || (micId ? 'Selected microphone' : 'System default')}
                  </span>
                </Button>
              }
            />
          </div>

          {isVideo && (
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs text-muted-foreground">Camera device</Label>
              <DeviceMenu
                kind="videoinput"
                devices={devices.videoinput}
                currentId={camId}
                onSelect={(id) => { void switchCamLobby(id); }}
                onEnsureLabels={() => { void ensureLabels(); }}
                disabled={acquiring}
                triggerLabel="Camera"
                triggerVariant="button"
                hasLabels={hasLabels}
                trigger={
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 max-w-[200px] truncate justify-start gap-1.5"
                    data-testid="lobby-device-picker-cam"
                    disabled={acquiring}
                  >
                    <Video className="h-3 w-3 shrink-0" />
                    <span className="truncate text-[11px]">
                      {devices.videoinput.find((d) => d.deviceId === camId)?.label
                        || (camId ? 'Selected camera' : 'System default')}
                    </span>
                  </Button>
                }
              />
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs text-muted-foreground">Speaker device</Label>
            <DeviceMenu
              kind="audiooutput"
              devices={devices.audiooutput}
              currentId={speakerId}
              onSelect={(id) => { switchSpeakerLobby(id); }}
              onEnsureLabels={() => { void ensureLabels(); }}
              disabled={acquiring}
              unsupported={!speakerSupported}
              unsupportedReason="Speaker selection isn't supported in this browser. Try Chrome, Edge, or Opera."
              triggerLabel="Speaker"
              triggerVariant="button"
              hasLabels={hasLabels}
              trigger={
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 max-w-[200px] truncate justify-start gap-1.5"
                  data-testid="lobby-device-picker-speaker"
                  disabled={acquiring || !speakerSupported}
                  title={!speakerSupported
                    ? "Speaker selection isn't supported in this browser. Try Chrome, Edge, or Opera."
                    : undefined}
                >
                  <Phone className="h-3 w-3 shrink-0" />
                  <span className="truncate text-[11px]">
                    {devices.audiooutput.find((d) => d.deviceId === speakerId)?.label
                      || (speakerId ? 'Selected speaker' : 'System default')}
                  </span>
                </Button>
              }
            />
          </div>
        </div>

        {/* Skip-lobby preference */}
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <Checkbox
            checked={skipNextTime}
            onCheckedChange={(c) => setSkipNextTime(c === true)}
            data-testid="lobby-skip-checkbox"
          />
          Skip device check next time and join immediately with these devices
        </label>
        </>
        )}

        {/* Action buttons. Two distinct shapes:
            - permission panel up → Cancel + Try Again (re-runs acquire)
            - normal             → Cancel + Join / Join with audio only */}
        <div className="flex gap-3 mt-1">
          <Button
            variant="outline"
            className="flex-1 gap-2"
            onClick={handleCancel}
            disabled={joining}
            data-testid="lobby-cancel"
          >
            <PhoneOff className="h-4 w-4" /> Cancel
          </Button>
          {showPermissionPanel ? (
            <Button
              className="flex-1 gap-2"
              onClick={() => { void acquireTracks(); }}
              disabled={acquiring}
              data-testid="lobby-retry"
            >
              {acquiring
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <RefreshCw className="h-4 w-4" />}
              {acquiring ? 'Checking…' : 'Try Again'}
            </Button>
          ) : (
            <Button
              className="flex-1 gap-2 bg-green-600 hover:bg-green-700 text-white"
              onClick={() => { void handleJoin(); }}
              disabled={joining || acquiring || !canJoinAudio}
              data-testid="lobby-join"
              title={!canJoinAudio
                ? 'Microphone unavailable — fix your microphone access to join.'
                : undefined}
            >
              {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
              {joining
                ? 'Joining…'
                : audioOnlyVideoJoin
                  ? 'Join with audio only'
                  : 'Join'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
